import { useState } from 'react';
import { PageHero } from '@components/common';
import {
  Send,
  CheckCircle,
  Mail,
  Briefcase,
  Shield,
  FileText,
  Users,
  Calendar,
  TrendingUp,
} from 'lucide-react';

export default function CisoSignupPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    fetch('/', {
      method: 'POST',
      body: formData,
    })
      .then(() => {
        setIsSubmitted(true);
      })
      .catch(error => {
        console.error('Form submission error:', error);
      });
  };

  return (
    <>
      <PageHero
        title="Join Our CISO Network"
        subtitle="Register your interest to work with us as a fractional CISO. We'll be in touch when the right opportunity comes up."
        eyebrow="For CISOs"
        seoTitle="Join Our CISO Network | The CISO Network"
        seoDescription="Sign up to join our network of fractional CISOs. We match experienced security leaders with organisations that need top-tier security expertise."
      />

      {/* Why Join Section */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Join the CISO Network?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We provide the infrastructure, support, and community you need to succeed as an
              independent security leader.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Mail,
                title: 'Corporate Email',
                description:
                  'Professional @thecisonetwork.com email address to maintain credibility with clients and partners.',
              },
              {
                icon: Briefcase,
                title: 'Business Development',
                description:
                  'We handle sales, marketing, and client acquisition. You focus on delivering exceptional security leadership.',
              },
              {
                icon: Shield,
                title: 'Protected Reputation',
                description:
                  'We vet every engagement to ensure it is the right fit. No taking on work that could harm your credibility or career.',
              },
              {
                icon: FileText,
                title: 'Documentation Library',
                description:
                  'Access our shared templates, frameworks, policies, and tooling to deliver value from day one on every engagement.',
              },
              {
                icon: Users,
                title: 'Peer Network',
                description:
                  'Connect with fellow fractional CISOs for advice, peer review, and support on complex security challenges.',
              },
              {
                icon: CheckCircle,
                title: 'Operational Support',
                description:
                  'We handle contracting, invoicing, and client relationships so you do not have to.',
              },
              {
                icon: Calendar,
                title: 'Leave Cover',
                description:
                  'Need time off? We arrange qualified cover from within the network so your clients stay protected and you can truly switch off.',
              },
              {
                icon: TrendingUp,
                title: 'Continuous Pipeline',
                description:
                  'When one engagement ends, we already have the next opportunity lined up. No gaps in income, no frantic job searching.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-card rounded-2xl p-8 border border-border text-center hover:border-teal/50 transition-colors"
              >
                <div className="h-14 w-14 rounded-xl bg-teal flex items-center justify-center mx-auto mb-6">
                  <item.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground mb-3">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                Ready to Join?
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Tell us about yourself and we'll be in touch when the right opportunity comes up.
              </p>
            </div>
            <div className="bg-card rounded-2xl p-6 lg:p-8 border border-border">
              {isSubmitted ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-teal/20 flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-teal" />
                  </div>
                  <h4 className="font-heading text-xl font-bold text-foreground mb-2">
                    Application Received
                  </h4>
                  <p className="text-muted-foreground max-w-sm">
                    Thank you for your interest. We'll be in touch when we have the right
                    opportunity for you.
                  </p>
                  <p className="text-muted-foreground max-w-sm">
                    In the meantime, follow us on{' '}
                    <a
                      href="https://linkedin.com/company/thecisonetwork"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal hover:underline font-medium"
                    >
                      LinkedIn
                    </a>{' '}
                    to stay across the latest from the network.
                  </p>
                </div>
              ) : (
                <form
                  name="ciso-signup"
                  method="POST"
                  data-netlify="true"
                  encType="multipart/form-data"
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <input type="hidden" name="form-name" value="ciso-signup" />

                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-colors"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="location"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      Location <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-colors"
                      placeholder="London, UK"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="linkedin"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      LinkedIn Profile{' '}
                      <span className="text-muted-foreground font-normal">(optional)</span>
                    </label>
                    <input
                      type="url"
                      id="linkedin"
                      name="linkedin"
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-colors"
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                  </div>

                  <div>
                    <label htmlFor="cv" className="block text-sm font-medium text-foreground mb-2">
                      CV / Resume{' '}
                      <span className="text-muted-foreground font-normal">(optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        id="cv"
                        name="cv"
                        accept=".pdf,.doc,.docx"
                        className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-teal/10 file:text-teal hover:file:bg-teal/20 transition-colors"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Accepted formats: PDF, DOC, DOCX
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="experience"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      Brief Experience Summary{' '}
                      <span className="text-muted-foreground font-normal">(optional)</span>
                    </label>
                    <textarea
                      id="experience"
                      name="experience"
                      rows={4}
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-colors resize-none"
                      placeholder="Tell us a little about your background, availability, and the kind of roles you're looking for..."
                    />
                  </div>

                  <button type="submit" className="w-full btn-hero justify-center">
                    <Send className="w-4 h-4 mr-2" />
                    Submit Application
                  </button>

                  <p className="text-xs text-muted-foreground text-center">
                    By submitting, you agree to our{' '}
                    <a
                      href="/privacy-policy"
                      className="underline hover:text-teal transition-colors"
                    >
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a
                      href="/terms-of-service"
                      className="underline hover:text-teal transition-colors"
                    >
                      Terms of Service
                    </a>
                    .
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
